import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from './config';
import { DateTime } from 'luxon';
import { getApplicationDisplayName } from './multiAppUtils';
import BrandHeader from './BrandHeader';

const SchedulesView = ({ jwt }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [cancelingId, setCancelingId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [loadingToggles, setLoadingToggles] = useState({});
  const [userApplications, setUserApplications] = useState([]);

  // Weekday names for display
  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Load user applications for icon display
  useEffect(() => {
    const loadApplications = async () => {
      try {
        const response = await axios.get(`${API_BASE}/applications`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setUserApplications(response.data.applications || []);
      } catch (err) {
        console.error('Failed to load applications:', err);
      }
    };

    if (jwt) {
      loadApplications();
    }
  }, [jwt]);

  // Helper function to get app info from loaded applications
  const getAppInfo = (appId) => {
    return userApplications.find(app => app.app_id === appId) || null;
  };

  // Function to get sortable date for a schedule
  const getSortableDate = (schedule) => {
    if (schedule.type === 'one_time') {
      // For one-time schedules, use the actual date
      return new Date(schedule.date_time.date);
    } else if (schedule.type === 'recurring') {
      // For recurring schedules, convert to current week's date
      const patternDate = new Date(schedule.date_time.date);
      const patternDayOfWeek = patternDate.getDay(); // 0-6 (Sunday-Saturday)
      const patternTime = patternDate.toTimeString().split(' ')[0]; // HH:MM:SS
      
      // Get current date
      const now = new Date();
      const currentDayOfWeek = now.getDay();
      
      // Calculate days to add to get to the target weekday
      let daysToAdd = patternDayOfWeek - currentDayOfWeek;
      if (daysToAdd <= 0) {
        // If the target day is today or earlier this week, move to next week
        daysToAdd += 7;
      }
      
      // Create the date for this week's occurrence
      const thisWeekDate = new Date(now);
      thisWeekDate.setDate(now.getDate() + daysToAdd);
      
      // Set the time to match the pattern
      const [hours, minutes, seconds] = patternTime.split(':').map(Number);
      thisWeekDate.setHours(hours, minutes, seconds, 0);
      
      return thisWeekDate;
    }
    return new Date(0); // Fallback for unknown types
  };

  // Function to sort schedules by date
  const sortSchedulesByDate = useCallback((schedulesArray) => {
    return [...schedulesArray].sort((a, b) => {
      const dateA = getSortableDate(a);
      const dateB = getSortableDate(b);
      return dateA - dateB;
    });
  }, []);

  useEffect(() => {
    const fetchSchedules = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axios.get(`${API_BASE}/schedule/mine`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        // Sort schedules by date before setting state
        const sortedSchedules = sortSchedulesByDate(resp.data);
        setSchedules(sortedSchedules);
      } catch (err) {
        setError(
          err?.response?.data?.detail || err?.message || 'Failed to fetch schedules.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSchedules();
  }, [jwt, sortSchedulesByDate]);

  useEffect(() => {
    // Fetch boxes for box name lookup (from all apps)
    const fetchBoxes = async () => {
      try {
        const resp = await axios.get(`${API_BASE}/boxes/all-apps`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        // /boxes/all-apps returns boxes directly, not nested in memberships
        setBoxes(resp.data.data || []);
      } catch (err) {
        setBoxes([]);
      }
    };
    fetchBoxes();
  }, [jwt]);

  const getBoxName = (boxId) => {
    const box = boxes.find(b => b.id === boxId);
    return box ? box.name : `Box ${boxId}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  };

  const handleCancel = async (schedule_id) => {
    setCancelingId(schedule_id);
    setToast({ message: '', type: '' });
    try {
      await axios.post(`${API_BASE}/schedule/cancelSchedule`, { schedule_id }, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setSchedules(prev => prev.filter(s => s.schedule_id !== schedule_id));
      setToast({ message: 'Schedule cancelled successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.response?.data?.detail || err?.message || 'Failed to cancel schedule.', type: 'error' });
    } finally {
      setCancelingId(null);
      setConfirmCancel(null);
    }
  };

  const handleToggleStatus = async (schedule_id, currentStatus) => {
    setToast({ message: '', type: '' });
    // Optimistically update UI for toggle
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    setSchedules(prev => {
      const updated = prev.map(s => s.schedule_id === schedule_id ? { ...s, status: newStatus } : s);
      return sortSchedulesByDate(updated);
    });
    setLoadingToggles(prev => ({ ...prev, [schedule_id]: true }));
    try {
      await axios.post(`${API_BASE}/schedule/updateStatus`, { schedule_id, status: newStatus }, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setLoadingToggles(prev => ({ ...prev, [schedule_id]: false }));
      // No success toast
    } catch (err) {
      // Revert UI if error
      setSchedules(prev => {
        const reverted = prev.map(s => s.schedule_id === schedule_id ? { ...s, status: currentStatus } : s);
        return sortSchedulesByDate(reverted);
      });
      setLoadingToggles(prev => ({ ...prev, [schedule_id]: false }));
      setToast({ message: err?.response?.data?.detail || err?.message || 'Failed to update schedule status.', type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbff] py-4 md:py-8 box-border">
      <div className="max-w-4xl mx-auto px-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(31,41,55,0.10)] w-full box-border">
          <BrandHeader title="My Schedules" />
          <div className="pb-6 md:p-8 w-full box-border">
            {toast.message && (
              <div className={`mb-4 w-full px-4 py-2 rounded text-center font-semibold ${toast.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>{toast.message}</div>
            )}
            {loading && <div className="text-gray-500 text-center py-8">Loading schedules...</div>}
            {error && <div className="text-red-600 mb-4 text-center">{error}</div>}
            {!loading && !error && schedules.length === 0 && (
              <div className="text-gray-500 text-center py-8">No schedules found.</div>
            )}
            {!loading && !error && schedules.length > 0 && (
              <div className="w-full max-w-2xl mx-auto space-y-4">
                {schedules.map((sched) => (
                  <div key={sched.schedule_id} className="border rounded-lg p-4 bg-white shadow-sm relative flex flex-col">
                    {/* Elegant Cancel button and toggle switch (toggle only for recurring) */}
                    <div className="absolute top-2 right-2 flex items-center gap-2">
                      {sched.type === 'recurring' && (
                        <label className="flex items-center cursor-pointer select-none">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={sched.status === 'active'}
                              onChange={() => handleToggleStatus(sched.schedule_id, sched.status)}
                              className="sr-only"
                            />
                            <div className={`block w-10 h-6 rounded-full transition-colors duration-200 ${sched.status === 'active' ? 'bg-green-400' : 'bg-gray-300'} opacity-100`}></div>
                            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow transition-transform duration-200 ${sched.status === 'active' ? 'translate-x-4' : ''} opacity-100`}></div>
                          </div>
                        </label>
                      )}
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 hover:text-red-800 shadow-sm transition-colors focus:outline-none"
                        title="Cancel schedule"
                        onClick={() => setConfirmCancel(sched.schedule_id)}
                        disabled={cancelingId === sched.schedule_id}
                        style={{ cursor: 'pointer', lineHeight: 1 }}
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="9" fill="none"/>
                          <path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                    {/* Confirm cancel toast/modal */}
                    {confirmCancel === sched.schedule_id && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                        <div className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full flex flex-col items-center">
                          <div className="text-lg font-bold mb-4 text-gray-800">Cancel this schedule?</div>
                          <div className="mb-6 text-gray-600 text-center">Are you sure you want to remove this schedule?</div>
                          <div className="flex gap-3 w-full">
                            <button
                              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                              onClick={() => setConfirmCancel(null)}
                            >
                              No
                            </button>
                            <button
                              className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                              onClick={() => handleCancel(sched.schedule_id)}
                              disabled={cancelingId === sched.schedule_id}
                            >
                              {cancelingId === sched.schedule_id ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      {/* App Icon */}
                      {(() => {
                        const appId = sched.app_id || 'default';
                        const appInfo = getAppInfo(appId);
                        const iconPath = appInfo?.icon_path;
                        const appName = appInfo?.name || appId;

                        return iconPath ? (
                          <img
                            src={iconPath}
                            alt={appName}
                            className="w-6 h-6 rounded object-cover flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-500 font-semibold text-xs">
                              {appName.charAt(0)}
                            </span>
                          </div>
                        );
                      })()}
                      <span className="font-bold text-lg text-gray-800">{sched.class_name}</span>
                      <span className={`font-semibold text-xs px-2 py-1 rounded ${sched.type === 'one_time' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}
                      >
                        {sched.type === 'one_time' ? 'One Time' : 'Recurring'}
                      </span>
                    </div>
                    {/* Automation run time */}
                    {(() => {
                      if (!sched.next_automation_run) {
                        return (
                          <div className="mb-1 text-xs text-red-700 font-semibold">
                            Error: Could not calculate next automation run
                          </div>
                        );
                      }
                      const dt = DateTime.fromISO(sched.next_automation_run);
                      const automationTime = dt.isValid ? dt.toFormat('cccc, dd LLL yyyy HH:mm') : sched.next_automation_run;
                      return (
                        <div className="mb-1 text-xs text-blue-700">
                          <span className="font-semibold">Automation run:</span> {automationTime}
                        </div>
                      );
                    })()}
                    <div className="text-sm text-gray-600 mb-1">
                      Box: {getBoxName(sched.box_id)}
                    </div>
                    <div className="text-xs text-gray-500 mb-1">
                      App: {getApplicationDisplayName(sched.app_id || 'default')}
                    </div>
                    {sched.type === 'one_time' && sched.one_time && (
                      <div className="text-sm text-gray-700">
                        {(() => {
                          const dateTimeStr = sched.date_time.date;
                          const dateObj = new Date(dateTimeStr);
                          const dayName = weekdayNames[dateObj.getDay()];
                          const timeStr = dateTimeStr.split(' ')[1].substring(0, 5); // Extract HH:MM from "YYYY-MM-DD HH:MM:SS"
                          const dateStr = dateTimeStr.split(' ')[0]; // Extract YYYY-MM-DD
                          return `One Time: ${dayName}, ${dateStr} at ${timeStr}`;
                        })()}
                      </div>
                    )}
                    {sched.type === 'recurring' && sched.pattern && (
                      <div className="text-sm text-gray-700">
                        {(() => {
                          const dateTimeStr = sched.date_time.date;
                          const dateObj = new Date(dateTimeStr);
                          const dayName = weekdayNames[dateObj.getDay()];
                          const timeStr = dateTimeStr.split(' ')[1].substring(0, 5); // Extract HH:MM from "YYYY-MM-DD HH:MM:SS"
                          return `Recurs every week on ${dayName} at ${timeStr}`;
                        })()}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-2">Created: {formatDateTime(sched.created_at)}</div>
                    <div className={`text-xs font-semibold mt-1 ${sched.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'} px-2 py-1 rounded inline-block w-fit`}
                    >
                       {loadingToggles[sched.schedule_id] ? (
                         <span className="flex items-center gap-1"><svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>Updating...</span>
                       ) : (
                         <>Status: {sched.status}</>
                       )}
                    </div>
                     {/* Note for paused recurring schedules, but only after BE responds */}
                     {sched.type === 'recurring' && sched.status === 'paused' && !loadingToggles[sched.schedule_id] && (
                       <div className="mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                         This class won't be booked as long as the schedule is disabled.
                       </div>
                     )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulesView;
